import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FeatureRequest, FeatureRequestKey } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import {
  featureRequestKeys,
  listFeatureRequests,
  requestFeature,
} from '../api/feature-requests';

/**
 * Whether the signed-in person has asked for `feature`, and a way to ask.
 * The answer lives on the server, so it follows them across browsers.
 */
export function useFeatureRequest(
  feature: FeatureRequestKey,
  projectId?: string,
) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryFn: listFeatureRequests,
    queryKey: featureRequestKeys.mine(),
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: () =>
      requestFeature({ feature, ...(projectId ? { projectId } : {}) }),
    onSuccess: (row) => {
      trackEvent('feature_requested', { feature });
      queryClient.setQueryData<FeatureRequest[]>(
        featureRequestKeys.mine(),
        (current = []) => [
          ...current.filter((existing) => existing.id !== row.id),
          row,
        ],
      );
    },
  });

  return {
    failed: mutation.isError,
    request: () => {
      mutation.mutate();
    },
    requested: (data ?? []).some((row) => row.feature === feature),
    requesting: mutation.isPending,
  };
}
